/**
 * The six-by-seven grid a month is drawn on.
 *
 * Lifted out of the calendar page so it can be tested without a browser. The
 * page had it inline, along with a `TODAY` frozen at the 8th of September —
 * a fixture date from the design template that shipped as the real thing, so
 * the calendar highlighted a day that had stopped being today a long time ago.
 *
 * Always six rows, even when five would do. A grid that changes height as you
 * move through the year makes everything below it jump, and the jump reads as
 * the page reloading.
 */

/** Sunday or Monday. Nobody means Wednesday. */
export type WeekStart = 0 | 1;

export interface DayCell {
  date: Date;
  /** False for the leading and trailing days borrowed from the neighbours. */
  inMonth: boolean;
}

const NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** Column headings, rotated to match where the week starts. */
export function weekdayNames(weekStartsOn: WeekStart): string[] {
  return Array.from({ length: 7 }, (_, i) => NAMES[(i + weekStartsOn) % 7]!);
}

/**
 * Every cell of the grid for one month, in reading order.
 *
 * @param month  zero-based, the way `Date` counts them.
 */
export function monthMatrix(year: number, month: number, weekStartsOn: WeekStart): DayCell[] {
  const firstDay = new Date(year, month, 1);
  // `getDay()` counts from Sunday whatever the locale, so the offset is how
  // far the first of the month sits from the column the week opens in.
  const offset = (firstDay.getDay() - weekStartsOn + 7) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: DayCell[] = [];
  for (let i = 0; i < offset; i += 1) {
    cells.push({ date: new Date(year, month, i - offset + 1), inMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push({ date: new Date(year, month, d), inMonth: true });
  }
  while (cells.length < 42) {
    const last = cells[cells.length - 1]!.date;
    cells.push({
      date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1),
      inMonth: false
    });
  }
  return cells;
}

/** The seven days of the week `date` falls in, starting where the week starts. */
export function weekAround(date: Date, weekStartsOn: WeekStart): Date[] {
  const back = (date.getDay() - weekStartsOn + 7) % 7;
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate() - back);
  return Array.from(
    { length: 7 },
    (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
  );
}
